// costReport.js — the pure logic behind รายงานต้นทุน (ReportProductCost.vue).
//
// Spec: docs/specs/2026-09-22-product-cost-report-design.md §7.2 (the screen,
// the export, the file name), §8 (the invariants the numbers must satisfy) and
// §13.7 (round 2: the backfill button and the line-owned SKU row).
// Issues asrth/mdf-bo-fn#265 (round 1), #267 (round 2).
//
// ROUND 2 CHANGED WHAT "COST" MEANS, not what this file computes: the API now
// reports the value snapshotted when the order line was written instead of the
// SKU's current cost (bo-api §13.6). The arithmetic here is unaffected — the
// shapes are identical — but the screen gained a button that rewrites those
// snapshots for a date range, and a tag for the rows whose name comes from the
// order line rather than from a SKU record.
//
// WHY THIS FILE EXISTS AT ALL
// ---------------------------------------------------------------------------
// This repo cannot mount .vue files under jest (see the memory note
// reference-bo-fn-jest-infra), so anything left inside the SFC can only ever be
// tested by reading the file as text. Everything here is therefore plain JS with
// no Vue, no i18n and no network: the drill-down state machine, the totals
// reducer, the two sheet builders and the file name — i.e. every part of the
// screen that can be WRONG rather than merely ugly.
//
// FIELD NAMES ARE COPIED FROM THE GO HANDLER, not invented here
// (controllers/BO-ReportCost.go + BO-ReportCost_struct.go):
//
//   root row       key, name, orders, qty, sales, cost, margin, missing_cost_sku
//   promotion row  bo_product_id, name, platform_product_id, orders, qty, sales,
//                  cost, margin, missing_cost_sku
//   sku row        sku_id, sku_code, seller_sku, sku_name, qty, sales, unit_cost,
//                  cost, margin, cost_missing
//   order line     order_id, order_no, created_at, platform_id, platform_name,
//                  sale_channel_id, sale_channel_name, bo_product_id,
//                  bo_product_name, sku_id, sku_code, qty, unit_price, sales,
//                  unit_cost, cost, margin, cost_missing
//
// qty/orders/ids are NUMBERS and money is a NUMBER; only names, codes,
// created_at and platform_product_id are strings. The sheets keep numbers as
// numbers so Excel can sum a column the reader selects — a formatted string
// would turn every total in the file into text.

export const LEVEL_ROOT = "root";
export const LEVEL_PROMOTION = "promotion";
export const LEVEL_SKU = "sku";

export const VIEW_PLATFORM = "platform";
export const VIEW_CHANNEL = "channel";

// COST_REPORT_LINE_LIMIT mirrors CostReportLineLimit in the Go handler. It is
// here so the screen can say the number in its warning without waiting for a
// 413 to tell it.
export const COST_REPORT_LINE_LIMIT = 50000;

// COST_REPORT_MAX_DAYS is the widest range this SCREEN offers, counted in
// INCLUSIVE days: 2026-09-01 … 2026-09-07 is seven days and is allowed, the 8th
// is not.
//
// The API still accepts 92 days (bo-api #594 §6.1 — unchanged, and this change
// does not touch bo-api). The FE is deliberately the stricter side: the owner
// asked for a week because a 92-day query on this report is minutes of SQL and
// a browser that looks hung, not because the server would refuse it. So a range
// between 8 and 92 days is now rejected HERE and never reaches the handler —
// the one case where the comment on rangeErrorCode's "never refuse what the API
// would accept" no longer holds, and it is a product decision rather than an
// accident.
//
// NOTE the unit changed with the number: this constant used to be the EXCLUSIVE
// day distance (to − from). Anything reading it as such would be off by one, so
// it is only ever used through rangeErrorCode / the two disabled-date helpers
// below, which all count inclusively.
export const COST_REPORT_MAX_DAYS = 7;

/**
 * COST_REPORT_BUSY_MIN_MS — the least time the blocking modal stays up.
 *
 * An anti-flicker floor, nothing more: a modal that blinks for 80ms reads as a
 * glitch, not as feedback, and 350 is el-dialog's 300ms enter animation plus
 * a frame. (It was first added on the belief that a fast answer stranded the
 * modal on stg. That was an artefact of the automation tab being HIDDEN —
 * Chrome freezes CSS animations in a hidden tab, so Vue never receives the
 * animationend that ends the leave. A visible tab never had the problem.)
 */
export const COST_REPORT_BUSY_MIN_MS = 350;

/**
 * busyHoldMs — how much longer the modal must stay up, given when it was raised.
 * 0 means "lower it now". Pure, so the wait is testable without a clock.
 */
export function busyHoldMs(raisedAt, now, minMs = COST_REPORT_BUSY_MIN_MS) {
	const up = num(now) - num(raisedAt);
	if (!Number.isFinite(up) || up < 0) return 0;
	return Math.max(0, num(minMs) - up);
}

// COST_REPORT_DEFAULT_DAYS is the range the screen opens on: today and the two
// days before it (defaultCostRange).
export const COST_REPORT_DEFAULT_DAYS = 3;

// The report is read by people sitting in Bangkok and the API's day boundaries
// are Bangkok's, so "today" here is Asia/Bangkok's today — never the browser's
// and never UTC's. Thailand has had a fixed +07:00 offset since 1920 and no
// DST, so a constant is correct and does not need Intl (which, in this Node 16
// / jsdom test environment, is not guaranteed to carry the tz database).
const BANGKOK_OFFSET_MS = 7 * 60 * 60 * 1000;

const bangkokYmd = (ms) => new Date(ms + BANGKOK_OFFSET_MS).toISOString().slice(0, 10);

/**
 * defaultCostRange returns { timeFrom, timeTo } for the last COST_REPORT_DEFAULT_DAYS
 * days INCLUSIVE — today plus the two days before it — as YYYY-MM-DD in Bangkok.
 *
 * `now` is an argument rather than a `new Date()` inside so the caller can be
 * tested at a chosen instant; the page passes the real clock.
 */
export function defaultCostRange(now) {
	const raw =
		now instanceof Date ? now.getTime() : typeof now === "number" ? now : Date.parse(now);
	const ms = Number.isFinite(raw) ? raw : Date.now();
	// Subtracting whole days in absolute milliseconds is exact here precisely
	// because the offset is fixed: no DST day is 23 or 25 hours long in Bangkok.
	return {
		timeFrom: bangkokYmd(ms - (COST_REPORT_DEFAULT_DAYS - 1) * 86400000),
		timeTo: bangkokYmd(ms),
	};
}

/** num coerces anything the API may hand us into a finite number. */
export function num(v) {
	const n = typeof v === "number" ? v : parseFloat(v);
	return Number.isFinite(n) ? n : 0;
}

/** round2 is the FE twin of the handler's emit-time rounding. */
export function round2(v) {
	return Math.round(num(v) * 100) / 100;
}

/**
 * sumRows adds up the four ADDITIVE columns of any level.
 *
 * `orders` and `missing_cost_sku` are deliberately NOT summed: the server
 * computes them as COUNT(DISTINCT …) over the whole level, so an order spanning
 * two rows would be counted twice here (spec §8.8 says so in as many words, and
 * calls a test that demands they add up "เขียนผิด"). The screen therefore takes
 * those two from the response's `total`, never from this function.
 */
export function sumRows(rows) {
	const out = { qty: 0, sales: 0, cost: 0, margin: 0 };
	(rows || []).forEach((r) => {
		out.qty += num(r.qty);
		out.sales += num(r.sales);
		out.cost += num(r.cost);
		out.margin += num(r.margin);
	});
	out.qty = Math.round(out.qty);
	out.sales = round2(out.sales);
	out.cost = round2(out.cost);
	out.margin = round2(out.margin);
	return out;
}

/**
 * marginPct is margin ÷ sales as a percentage, 2 decimals.
 *
 * Zero sales returns 0 rather than NaN/Infinity: a range with no sales is a
 * normal answer (a quiet month), not an error, and "NaN%" on the summary strip
 * is the kind of thing that gets reported as a broken report.
 */
export function marginPct(sales, cost) {
	const s = num(sales);
	if (s === 0) return 0;
	return Math.round(((s - num(cost)) / s) * 10000) / 100;
}

/**
 * showExpense — the รายจ่าย / กำไรสุทธิ columns and tiles exist ONLY at the
 * root level of view=platform (bo-api 2026-10-26: account-book expenses bound
 * to a platform). Product / SKU drill-down and the channel view never show
 * them, and the API omits the fields there.
 */
export function showExpense(level, view) {
	return level === LEVEL_ROOT && view === VIEW_PLATFORM;
}

/**
 * netProfit reads the server's net_profit; when absent (older API) it is
 * margin − expense, never sales − cost alone.
 */
export function netProfit(r) {
	if (!r) return 0;
	if (r.net_profit !== undefined && r.net_profit !== null) return num(r.net_profit);
	return round2(num(r.margin) - num(r.expense));
}

/**
 * exportFileName builds `cost_<view>_<level>_<from>_<to>.xlsx` (§7.2).
 * The dates are the YYYY-MM-DD the user picked, not the filled-in timestamps.
 */
export function exportFileName({ view, level, timeFrom, timeTo }) {
	return `cost_${view}_${level}_${timeFrom}_${timeTo}.xlsx`;
}

// ---------------------------------------------------------------------------
// drill-down state
// ---------------------------------------------------------------------------
//
// The screen is one query with a growing WHERE, so the state is exactly the
// parameters of that WHERE. Keeping it in a reducer rather than in ad-hoc
// component fields is what makes "click ทั้งหมด after drilling twice" testable.

export function initialDrill() {
	return {
		level: LEVEL_ROOT,
		key: null,
		keyName: "",
		boProductId: null,
		boProductName: "",
	};
}

/** drillToPromotion — a click on a root row (a platform / sale channel). */
export function drillToPromotion(state, row) {
	return {
		...state,
		level: LEVEL_PROMOTION,
		key: num(row.key),
		keyName: row.name || "",
		boProductId: null,
		boProductName: "",
	};
}

/** drillToSku — a click on a promotion row. */
export function drillToSku(state, row) {
	return {
		...state,
		level: LEVEL_SKU,
		boProductId: num(row.bo_product_id),
		boProductName: row.name || "",
	};
}

/**
 * drillUp goes back to a level the breadcrumb offers.
 *
 * Going back to root clears the key as well: leaving a stale key behind is how a
 * breadcrumb ends up showing "ทั้งหมด" while the table still shows one platform.
 */
export function drillUp(state, level) {
	if (level === LEVEL_ROOT) return initialDrill();
	if (level === LEVEL_PROMOTION) {
		return { ...state, level: LEVEL_PROMOTION, boProductId: null, boProductName: "" };
	}
	return { ...state };
}

/**
 * breadcrumbItems renders the trail: ทั้งหมด › <platform> › <product>.
 * Every entry but the last is clickable; the last one is where we are.
 */
export function breadcrumbItems(state, allLabel) {
	const items = [{ level: LEVEL_ROOT, label: allLabel, active: state.level === LEVEL_ROOT }];
	if (state.level === LEVEL_PROMOTION || state.level === LEVEL_SKU) {
		items.push({
			level: LEVEL_PROMOTION,
			label: state.keyName,
			active: state.level === LEVEL_PROMOTION,
		});
	}
	if (state.level === LEVEL_SKU) {
		items.push({ level: LEVEL_SKU, label: state.boProductName, active: true });
	}
	return items;
}

/**
 * queryParams turns the drill state + filters into the query the four endpoints
 * take. `key`, `bo_product_id` and `sku_id` are only sent when they mean
 * something — 0 is a VALUE at this API (ไม่ระบุ channel, ไม่ทราบสินค้า,
 * ไม่ทราบ SKU), so "absent" cannot be spelled as 0.
 */
export function queryParams(state, { view, timeFrom, timeTo }) {
	const params = { view, time_from: timeFrom, time_to: timeTo };
	if (state.level !== LEVEL_ROOT && state.key !== null && state.key !== undefined) {
		params.key = state.key;
	}
	if (state.level === LEVEL_SKU && state.boProductId !== null && state.boProductId !== undefined) {
		params.bo_product_id = state.boProductId;
	}
	return params;
}

/**
 * orderExportParams is the query for sheet 2 (…/cost/orders).
 *
 * It is queryParams' narrowing seen from the export's side, and it exists as
 * its own function because the ROOT case is a rule rather than an accident:
 * the endpoint now accepts a request with NO key and answers with every line in
 * the range, so the root export must send none. Spelling "no filter" as
 * `key: 0` or `key: null` would mean "the channel whose id is 0" (ไม่ระบุ) — a
 * real selection at this API — and sheet 2 would quietly hold a subset of the
 * summary sheet printed next to it.
 *
 * At the SKU level the product is pinned explicitly: the drill state carries it
 * on `boProductId`, and the caller must not have to remember to copy it in.
 */
export function orderExportParams(state, filters) {
	const params = queryParams(state, filters);
	if (state.level === LEVEL_SKU && state.boProductId !== null && state.boProductId !== undefined) {
		params.bo_product_id = state.boProductId;
	}
	return params;
}

// ---------------------------------------------------------------------------
// sheets
// ---------------------------------------------------------------------------

/**
 * summarySheet builds sheet 1 — "the table that is on the screen", same columns
 * in the same order (§7.2). Labels come from the caller so this file stays free
 * of vue-i18n.
 *
 * labels: { key, name, platform_product_id, sku_code, seller_sku, orders, qty,
 *           sales, cost, margin, margin_pct, missing_cost_sku, cost_missing,
 *           unit_cost, yes }
 */
export function summarySheet(level, rows, labels, opts) {
	const L = labels || {};
	// withExpense: root level of view=platform only (see showExpense) — adds
	// รายจ่าย + กำไรสุทธิ after กำไร (screen order). Labels: expense, net_profit.
	const withExpense = !!(opts && opts.withExpense) && level === LEVEL_ROOT;
	return (rows || []).map((r) => {
		if (level === LEVEL_ROOT) {
			const row = {
				[L.name]: r.name,
				[L.orders]: num(r.orders),
				[L.qty]: num(r.qty),
				[L.sales]: num(r.sales),
				[L.cost]: num(r.cost),
				[L.margin]: num(r.margin),
			};
			// same order as the screen: กำไร, รายจ่าย, กำไรสุทธิ, % กำไร
			if (withExpense) {
				row[L.expense] = num(r.expense);
				row[L.net_profit] = netProfit(r);
			}
			row[L.margin_pct] = marginPct(r.sales, r.cost);
			row[L.missing_cost_sku] = num(r.missing_cost_sku);
			return row;
		}
		if (level === LEVEL_PROMOTION) {
			return {
				[L.name]: r.name,
				[L.platform_product_id]: r.platform_product_id,
				[L.orders]: num(r.orders),
				[L.qty]: num(r.qty),
				[L.sales]: num(r.sales),
				[L.cost]: num(r.cost),
				[L.margin]: num(r.margin),
				[L.margin_pct]: marginPct(r.sales, r.cost),
				[L.missing_cost_sku]: num(r.missing_cost_sku),
			};
		}
		return {
			[L.name]: r.sku_name,
			[L.sku_code]: r.sku_code,
			[L.seller_sku]: r.seller_sku,
			[L.qty]: num(r.qty),
			[L.sales]: num(r.sales),
			[L.unit_cost]: num(r.unit_cost),
			[L.cost]: num(r.cost),
			[L.margin]: num(r.margin),
			[L.margin_pct]: marginPct(r.sales, r.cost),
			[L.cost_missing]: r.cost_missing ? L.yes : "",
		};
	});
}

// ORDER_SHEET_COLUMNS is §7.2's list, in §7.2's order, as literals. It is NOT
// built from i18n: the spec pins these twelve headers and the file has to look
// the same to the person who opens it whichever language their UI was in.
export const ORDER_SHEET_COLUMNS = [
	"เลขที่ออเดอร์",
	"วันที่สร้าง",
	"Platform",
	"ช่องทางการขาย",
	"โปรโมชั่น",
	"sku_code",
	"จำนวน",
	"ราคาต่อหน่วย",
	"ยอดขาย",
	"ต้นทุนต่อหน่วย",
	"ต้นทุนรวม",
	"ต้นทุนไม่ครบ",
];

/**
 * orderSheet builds sheet 2 from …/cost/orders rows.
 *
 * `yesLabel` marks the ต้นทุนไม่ครบ column; false becomes "" rather than "ไม่",
 * so a reader can filter the column by "non-empty" and get exactly the lines
 * whose cost cannot be trusted.
 */
export function orderSheet(rows, yesLabel) {
	return (rows || []).map((r) => ({
		[ORDER_SHEET_COLUMNS[0]]: r.order_no,
		[ORDER_SHEET_COLUMNS[1]]: r.created_at,
		[ORDER_SHEET_COLUMNS[2]]: r.platform_name,
		[ORDER_SHEET_COLUMNS[3]]: r.sale_channel_name,
		[ORDER_SHEET_COLUMNS[4]]: r.bo_product_name,
		[ORDER_SHEET_COLUMNS[5]]: r.sku_code,
		[ORDER_SHEET_COLUMNS[6]]: num(r.qty),
		[ORDER_SHEET_COLUMNS[7]]: num(r.unit_price),
		[ORDER_SHEET_COLUMNS[8]]: num(r.sales),
		[ORDER_SHEET_COLUMNS[9]]: num(r.unit_cost),
		[ORDER_SHEET_COLUMNS[10]]: num(r.cost),
		[ORDER_SHEET_COLUMNS[11]]: r.cost_missing ? yesLabel : "",
	}));
}

/**
 * envelopeData unwraps go-core's HttpResult.
 *
 * `r.Set()` writes `Datum` (go-core HttpResult.go:80) while §6.1's examples
 * print the key as `Data`; both are accepted here so the screen does not depend
 * on which way that contradiction is settled in the API.
 */
export function envelopeData(res) {
	const body = (res && res.data) || {};
	if (body.Datum !== undefined && body.Datum !== null) return body.Datum;
	if (body.Data !== undefined && body.Data !== null) return body.Data;
	return {};
}

/**
 * isTooManyLines recognises the 413 of §6.1 on an axios error.
 * The caller must NOT build a partial file when this is true (§7.2).
 */
export function isTooManyLines(err) {
	const res = (err && err.response) || {};
	if (res.status === 413) return true;
	const body = res.data || {};
	return body.Msg === "cost_report_too_many_lines";
}

// ---------------------------------------------------------------------------
// errors
// ---------------------------------------------------------------------------
//
// The API answers a rejection with a machine code in `Msg`. That code is for
// this file, not for the operator: "cost_report_range_too_wide" in a swal title
// is a symptom, not a message — it does not say the fix is "pick a shorter
// range", and half of it is the name of a Go constant.
//
// COST_REPORT_ERROR_CODES is the whole contract (bo-api #594 §6.1). Each code
// has its own key under cost_report.errors.<code> in BOTH th.js and en.js, so
// the mapping below is mechanical and a missing translation is a test failure
// rather than a blank dialog.

export const COST_REPORT_ERROR_CODES = [
	"cost_report_bad_view",
	"cost_report_bad_range",
	"cost_report_range_too_wide",
	"cost_report_too_many_lines",
	"product_cost_forbidden",
];

/** costReportErrorI18nKey turns an axios error into { key, detail, code }.
 *
 * `detail` is EMPTY for every code we know — the translation already says
 * everything there is to say. For anything else (a 500, a proxy's HTML, a code
 * added on the API side after this bundle was built) the generic title carries
 * the raw Msg as a detail line, so the operator can read it to support without
 * it pretending to be the message.
 */
export function costReportErrorI18nKey(err) {
	const res = (err && err.response) || {};
	const body = res.data || {};
	const code = typeof body.Msg === "string" ? body.Msg : "";
	if (COST_REPORT_ERROR_CODES.indexOf(code) !== -1) {
		return { key: `cost_report.errors.${code}`, detail: "", code };
	}
	// A 413 is the one failure recognisable from the status alone, and it is
	// the one with an actionable message, so it is read even when the body did
	// not survive (a proxy answering the cap with its own page).
	if (res.status === 413) {
		return {
			key: "cost_report.errors.cost_report_too_many_lines",
			detail: "",
			code: "cost_report_too_many_lines",
		};
	}
	return { key: "cost_report.errors.unknown", detail: code, code };
}

/** ymdMs parses a plain YYYY-MM-DD as UTC midnight, or NaN.
 *
 * UTC on purpose: the strings are dates without a time, and local parsing would
 * make the day distance between two of them depend on the browser's timezone.
 * Both ends are parsed the same way, so the difference is exact whatever the
 * zone — this is a distance, not a timestamp, and the Bangkok offset applied in
 * defaultCostRange has no business here.
 */
function ymdMs(s) {
	if (typeof s !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return NaN;
	return Date.parse(`${s}T00:00:00Z`);
}

/** inclusiveDays counts both ends: 09-01 → 09-07 is 7. */
function inclusiveDays(fromMs, toMs) {
	return Math.round((toMs - fromMs) / 86400000) + 1;
}

/**
 * rangeErrorCode is the client-side pre-check, returning "" or one of the codes
 * above so the screen shows the SAME message it would have shown after a round
 * trip.
 *
 * The count is INCLUSIVE — from and to are both days the report covers — and is
 * compared against COST_REPORT_MAX_DAYS (7). See that constant for why the FE
 * is now stricter than the API's 92.
 */
export function rangeErrorCode(timeFrom, timeTo) {
	return rangeErrorCodeWithin(timeFrom, timeTo, COST_REPORT_MAX_DAYS);
}

/**
 * rangeErrorCodeWithin is rangeErrorCode with the cap as a parameter.
 *
 * The report and the backfill do not share a ceiling: the screen is capped at 7
 * INCLUSIVE days (COST_REPORT_MAX_DAYS) and the repair tool at 366 (bo-api
 * §13.5), because one of them builds a page and the other one fixes data. The
 * BUTTON on the report always sends the report's own range, so in practice it is
 * checked at 7 — but the number is passed in rather than assumed, so a caller
 * with a wider range is not refused by a limit that does not apply to it.
 *
 * `maxDays` is counted the way COST_REPORT_MAX_DAYS is: INCLUSIVE of both ends,
 * so 09-01 → 09-07 is 7 days and passes a cap of 7. It used to be the EXCLUSIVE
 * distance (to − from) on this branch; the inclusive count is what ships and what
 * the pickers below grey out, and the two must agree day for day or the calendar
 * offers a cell the pre-check then refuses.
 */
export function rangeErrorCodeWithin(timeFrom, timeTo, maxDays) {
	const from = ymdMs(timeFrom);
	const to = ymdMs(timeTo);
	if (!Number.isFinite(from) || !Number.isFinite(to)) return "cost_report_bad_range";
	if (to < from) return "cost_report_bad_range";
	if (inclusiveDays(from, to) > num(maxDays)) return "cost_report_range_too_wide";
	return "";
}

// ---------------------------------------------------------------------------
// the pickers
// ---------------------------------------------------------------------------
//
// A dialog saying "ช่วงวันที่เกิน 7 วัน" AFTER the operator picked the dates is
// the worst of both worlds, so the calendars grey out what rangeErrorCode would
// refuse. The two helpers take the Date the picker offers plus the OTHER end's
// YYYY-MM-DD string and answer the same question rangeErrorCode does — the test
// suite asserts they agree day by day around the boundary, because a date the
// calendar offers and the pre-check then rejects is a dead end for the user.
//
// An empty / unset other end disables nothing: the first pick must be free.

/** dateYmd turns the Date Element hands disabledDate into YYYY-MM-DD.
 *
 * The picker's Date is LOCAL midnight of the cell, so the local getters are the
 * ones that name the day the user is looking at; toISOString would shift the
 * cell by a day for every operator east of Greenwich, Bangkok included.
 */
function dateYmd(d) {
	if (!(d instanceof Date) || !Number.isFinite(d.getTime())) return "";
	const p = (n) => String(n).padStart(2, "0");
	// A Date built as `new Date("2026-09-07T00:00:00Z")` in a UTC test env and
	// one built by the picker in +07:00 both name the same cell this way.
	const utcMidnight = d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0;
	if (utcMidnight) return d.toISOString().slice(0, 10);
	return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** disabledFromDate — true when picking `date` as the START would be refused. */
export function disabledFromDate(date, timeTo) {
	const ymd = dateYmd(date);
	if (!ymd || !Number.isFinite(ymdMs(timeTo))) return false;
	return rangeErrorCode(ymd, timeTo) !== "";
}

/** disabledToDate — true when picking `date` as the END would be refused. */
export function disabledToDate(date, timeFrom) {
	const ymd = dateYmd(date);
	if (!ymd || !Number.isFinite(ymdMs(timeFrom))) return false;
	return rangeErrorCode(timeFrom, ymd) !== "";
}

// ---------------------------------------------------------------------------
// backfill — POST /api/v1/product-cost/backfill-orders (bo-api §13.5)
// ---------------------------------------------------------------------------
//
// Round 2 moved cost from "whatever the SKU costs right now" to a SNAPSHOT taken
// when the order line was written. The consequence the screen has to carry is
// that correcting a warehouse price NO LONGER changes last month's report — and
// this button is the only thing that does. It is therefore deliberately
// unglamorous: it takes the range that is already on the screen, it says that
// range out loud before it runs, and it reports four numbers afterwards.

// There is deliberately NO COST_REPORT_BACKFILL_MAX_DAYS here (#268). The API's
// own ceiling for this endpoint is 366 days (OrderCostBackfillMaxRangeDays), but
// the BUTTON is gated on badRange(), which checks the screen's range at the
// REPORT's 7 — so nothing on this side could ever reach a range the 366 would
// judge. A mirrored constant no caller passes is not documentation, it is a
// second copy of a server number free to drift out of step unnoticed; the cap
// stays a parameter of rangeErrorCodeWithin for any caller that genuinely has a
// wider range, and the server stays the authority on the boundary.

/**
 * backfillParams is the query for the backfill endpoint.
 *
 * It carries the DATES ONLY — no view, no key, no drill state. The endpoint
 * rewrites every line of every order created in the range, whatever platform,
 * channel, product or status it belongs to (§13.5: there is no order_status
 * filter either). Narrowing it to the drilled-into platform would leave the rest
 * of the range half repaired, and the totals on the screen would stop adding up
 * against the levels underneath them.
 */
export function backfillParams({ timeFrom, timeTo }) {
	return { time_from: timeFrom, time_to: timeTo };
}

/**
 * backfillResult normalises the endpoint's payload into the four counts plus
 * `skipped`, with every field present and numeric.
 *
 * `missing` and `unresolved` are different failures and both are shown:
 * `missing` means the line WAS written with a number we cannot fully trust,
 * `unresolved` means the line was left exactly as it was because its recipe
 * could not be rebuilt. Collapsing them into one "problems" figure would hide
 * the only one of the two an operator can act on.
 */
export function backfillResult(res) {
	const d = envelopeData(res) || {};
	return {
		scanned: num(d.scanned),
		updated: num(d.updated),
		missing: num(d.missing),
		unresolved: num(d.unresolved),
		skipped: d.skipped === true,
		time_from: typeof d.time_from === "string" ? d.time_from : "",
		time_to: typeof d.time_to === "string" ? d.time_to : "",
	};
}

/**
 * isLineOwnedSku — does this SKU-level row describe lines that carry no
 * bo_product_sku at all? (bo-api §13.6)
 *
 * Those are manual back-office orders with a dynamic SKU, and every Modern Trade
 * line. Round 1 showed them as one anonymous "ไม่ทราบ SKU" bucket at cost 0;
 * round 2 gives them their own name, their own sku_code and their own snapshot,
 * so they now look exactly like any other row — which is precisely why they need
 * a tag. Two things about them are still different and the operator has to know:
 * the identity comes from the ORDER LINE rather than from a SKU record, and the
 * grouping lumps every such line of the promotion into this one row (the orders
 * sheet is where they can be read individually).
 *
 * Only meaningful at the SKU level; `sku_id` does not exist at the other two.
 */
export function isLineOwnedSku(level, row) {
	if (level !== LEVEL_SKU) return false;
	return num((row || {}).sku_id) === 0;
}
